import { createOrgInputSchema, updateOrgInputSchema } from '@kiku/admin/types';
import { SUPPORTED_LANGUAGES } from '@kiku/config/constants';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { InferResponseType } from 'hono/client';
import { MoreVertical } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CrudDialog } from './crud-dialog';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { Input } from '~/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '~/components/ui/table';
import { api } from '~/lib/api';
import { meQueryOptions } from '~/lib/auth';
import { departmentsQueryOptions } from '~/lib/departments';
import { cn } from '~/lib/utils';
import { extractApiError } from '~/utils/api';
import { formatDate } from '~/utils/date';

type OrgsResponse = InferResponseType<typeof api.api.admin.organizations.$get>;
type Org = OrgsResponse['organizations'][number];

type OrgTableProps = {
  organizations: Org[];
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
};

const tenantsQueryOptions = queryOptions({
  queryKey: ['admin', 'tenants'] as const,
  queryFn: async () => {
    const res = await api.api.admin.tenants.$get();
    if (!res.ok) throw new Error('Failed to fetch tenants');
    return res.json();
  }
});

type TenantMode = 'new' | 'existing';

export function OrgTable({
  organizations,
  createOpen,
  onCreateOpenChange
}: OrgTableProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [editOrg, setEditOrg] = useState<Org | null>(null);

  const switchOrgMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const res = await api.api.admin.organizations.switch.$post({ json: { orgId } });
      if (!res.ok) {
        throw new Error(extractApiError(await res.json(), 'Failed to switch organization'));
      }
      return res.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: meQueryOptions.queryKey });
      await queryClient.invalidateQueries({ queryKey: departmentsQueryOptions.queryKey });
      void navigate({ to: '/jobs' });
    }
  });

  const createMutation = useMutation({
    mutationFn: async (
      values:
        | { tenantMode: 'new'; tenantName: string; name: string; slug: string }
        | { tenantMode: 'existing'; tenantId: string; name: string; slug: string }
    ) => {
      const res = await api.api.admin.organizations.$post({ json: values });
      if (!res.ok) {
        throw new Error(extractApiError(await res.json(), 'Failed to create organization'));
      }
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      onCreateOpenChange(false);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      values
    }: {
      id: string;
      values: {
        name?: string;
        slug?: string;
        language?: (typeof SUPPORTED_LANGUAGES)[number];
      };
    }) => {
      const res = await api.api.admin.organizations[':id'].$patch({ param: { id }, json: values });
      if (!res.ok) {
        throw new Error(extractApiError(await res.json(), 'Failed to update organization'));
      }
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      setEditOrg(null);
    }
  });

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.organizations.columns.tenant')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.organizations.columns.name')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.organizations.columns.slug')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.organizations.columns.createdAt')}
            </TableHead>
            <TableHead className="w-12 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.organizations.columns.actions')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {organizations.map((org) => (
            <TableRow key={org.id} className="group">
              <TableCell className="py-3 text-sm text-muted-foreground">{org.tenantName}</TableCell>
              <TableCell className="py-3">
                <Link
                  to="/admin/organizations/$orgId/departments"
                  params={{ orgId: org.id }}
                  className="text-sm text-primary hover:underline"
                >
                  {org.name}
                </Link>
              </TableCell>
              <TableCell className="py-3 text-sm text-muted-foreground">{org.slug}</TableCell>
              <TableCell className="py-3 text-sm text-muted-foreground">
                {formatDate(org.createdAt)}
              </TableCell>
              <TableCell className="py-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent">
                    <MoreVertical className="size-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        switchOrgMutation.mutate(org.id);
                      }}
                    >
                      {t('admin.organizations.columns.viewWorkspace')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setEditOrg(org);
                      }}
                    >
                      {t('actions.edit')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <CreateOrgDialog
        open={createOpen}
        onOpenChange={(open) => {
          onCreateOpenChange(open);
          if (!open) createMutation.reset();
        }}
        onSubmit={(values) => {
          createMutation.mutate(values);
        }}
        isPending={createMutation.isPending}
        error={createMutation.error?.message}
      />

      <CrudDialog
        open={editOrg !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditOrg(null);
            updateMutation.reset();
          }
        }}
        title={t('admin.organizations.editDialog.title')}
        fields={[
          {
            name: 'name',
            label: t('admin.organizations.columns.name'),
            placeholder: t('admin.organizations.createDialog.namePlaceholder')
          },
          {
            name: 'slug',
            label: t('admin.organizations.columns.slug'),
            placeholder: t('admin.organizations.createDialog.slugPlaceholder')
          },
          {
            name: 'language',
            label: t('admin.organizations.editDialog.language'),
            render: (value, onChange) => (
              <Select
                value={value}
                onValueChange={(v) => {
                  if (v) onChange(v);
                }}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t('admin.organizations.editDialog.languagePlaceholder')}
                  >
                    {value ? t(`languages.${value as 'en'}`) : null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <SelectItem key={lang} value={lang}>
                      {t(`languages.${lang as 'en'}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          }
        ]}
        defaultValues={{
          name: editOrg?.name ?? '',
          slug: editOrg?.slug ?? '',
          language: editOrg?.language ?? 'en'
        }}
        schema={updateOrgInputSchema}
        onSubmit={(values) => {
          if (!editOrg) return;
          updateMutation.mutate({
            id: editOrg.id,
            values: {
              name: values['name'],
              slug: values['slug'],
              language: values['language'] as (typeof SUPPORTED_LANGUAGES)[number] | undefined
            }
          });
        }}
        isPending={updateMutation.isPending}
        error={updateMutation.error?.message}
        submitLabel={t('actions.save')}
      />
    </>
  );
}

type CreateOrgDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    values:
      | { tenantMode: 'new'; tenantName: string; name: string; slug: string }
      | { tenantMode: 'existing'; tenantId: string; name: string; slug: string }
  ) => void;
  isPending?: boolean;
  error?: string | null;
};

function CreateOrgDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  error
}: CreateOrgDialogProps): React.ReactNode {
  const { t } = useTranslation();
  const { data: tenantsData } = useQuery({
    ...tenantsQueryOptions,
    enabled: open
  });
  const tenants = tenantsData?.tenants ?? [];

  const [tenantMode, setTenantMode] = useState<TenantMode>('new');
  const [tenantName, setTenantName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTenantMode('new');
      setTenantName('');
      setTenantId('');
      setName('');
      setSlug('');
      setValidationError(null);
    }
  }, [open]);

  function handleSubmit(e: { preventDefault: () => void }): void {
    e.preventDefault();
    setValidationError(null);

    const input =
      tenantMode === 'new'
        ? { tenantMode: 'new' as const, tenantName, name, slug }
        : { tenantMode: 'existing' as const, tenantId, name, slug };

    const result = createOrgInputSchema.safeParse(input);
    if (!result.success) {
      const issue = result.error.issues[0];
      setValidationError(issue?.message ?? t('validation.invalid'));
      return;
    }

    onSubmit(result.data);
  }

  const displayError = validationError ?? error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.organizations.createDialog.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">
              {t('admin.organizations.columns.tenant')}
            </label>
            <div className="flex gap-1 rounded-md border border-border p-1">
              <button
                type="button"
                className={cn(
                  'flex-1 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors',
                  tenantMode === 'new'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => {
                  setTenantMode('new');
                  setValidationError(null);
                }}
              >
                {t('admin.organizations.createDialog.newTenant')}
              </button>
              <button
                type="button"
                className={cn(
                  'flex-1 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors',
                  tenantMode === 'existing'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => {
                  setTenantMode('existing');
                  setValidationError(null);
                }}
              >
                {t('admin.organizations.createDialog.existingTenant')}
              </button>
            </div>
          </div>

          {tenantMode === 'new' ? (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="tenantName">
                {t('admin.organizations.createDialog.tenantName')}
              </label>
              <Input
                id="tenantName"
                placeholder={t('admin.organizations.createDialog.tenantNamePlaceholder')}
                value={tenantName}
                onChange={(e) => {
                  setTenantName(e.target.value);
                  setValidationError(null);
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="tenantId">
                {t('admin.organizations.createDialog.selectTenant')}
              </label>
              <Select
                value={tenantId}
                onValueChange={(value) => {
                  setTenantId(value ?? '');
                  setValidationError(null);
                }}
              >
                <SelectTrigger id="tenantId">
                  <SelectValue placeholder={t('admin.organizations.createDialog.selectTenant')} />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground" htmlFor="orgName">
              {t('admin.organizations.columns.name')}
            </label>
            <Input
              id="orgName"
              placeholder={t('admin.organizations.createDialog.namePlaceholder')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setValidationError(null);
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground" htmlFor="orgSlug">
              {t('admin.organizations.columns.slug')}
            </label>
            <Input
              id="orgSlug"
              placeholder={t('admin.organizations.createDialog.slugPlaceholder')}
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setValidationError(null);
              }}
            />
          </div>

          {displayError && <p className="text-sm text-destructive">{displayError}</p>}
          <DialogFooter>
            <Button type="submit" disabled={isPending}>
              {t('admin.organizations.newOrg')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
